import CardCommon from '@op/components/CardCommon';
import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import React, { useMemo, useRef, useState } from 'react';
import { useChannelShow, useEmptyText, useShortUrlFilterValue } from '../../hooks';
import {
  ExportDefaultType,
  PageFilterKeys,
} from '@op/components/FilterCommon/types';
import { Button, Card } from 'tea-component';
import { useLocation } from 'umi';
import { DBO_U } from '@op/utils/format';
import { PromptConfirmItemType } from '@op/components/GPT/types';
import ReportFilter from '@op/pages/AIAnalysisHub/AIInsightsReport/ReportFilter';
import {
  updateURL,
  useCustomInsightReportList,
} from '@op/pages/AIAnalysisHub/hooks';
import { CustomGptBody, GPTBody } from '@op/pages/AIAnalysisHub/components';
import { dayToDaily } from '@op/components/FilterCommon/constants/date';
import useLanguage from '@op/hooks/useLanguage';
import useReportLanguage from '@op/pages/report/hooks/useReportLanguage';

interface IPreviewFilter {
  gameInfo: any;
  shortId: string;
  wholeGameInfo: any;
  contentRef?: any;
  promptInfo: PromptConfirmItemType;
  showOnPage?: boolean;
  isSaved?: boolean;
  setPageStatus?: (status: string) => void;
  allPreview?: boolean;
}
export default function PreviewBody(props: IPreviewFilter) {
  const { gameInfo, shortId, contentRef, promptInfo, allPreview, wholeGameInfo } = props;
  const { loading, filter, pageName } = useShortUrlFilterValue({ id: shortId });
  const { commentList, socialList, backCondOriginData } = useChannelShow();

  /// TODO: filter确实game id
  const filterParams = useMemo(
    () => ({
      ...filter,
      ...gameInfo,
    }),
    [gameInfo, filter],
  );

  return (
    <CardCommon
      loading={loading}
      loadingHeight={600}
      contentCard
      className="dbo-custom-ai-summary-preview-filter"
    >
      <PreviewFilter
        gameInfo={gameInfo}
        pageName={pageName as any}
        shortId={shortId}
        backCondOriginData={backCondOriginData}
        isAdvanced={promptInfo.gptOptions === 'advanced'}
        allPreview={allPreview}
      ></PreviewFilter>

      <GPTBody
        filter={filterParams}
        contentRef={contentRef}
        commentList={commentList}
        socialList={socialList}
        promptInfo={promptInfo}
        gameInfo={gameInfo}
        openCardLoading
        hasStopBtn={false}
        wholeGameInfo={wholeGameInfo}
        isMini
        showStatsHeader
      ></GPTBody>
    </CardCommon>
  );
}

export function InsightPreviewBody(
  props: IPreviewFilter & { reportItem: any },
) {
  const {
    gameInfo,
    shortId,
    contentRef,
    reportItem,
    wholeGameInfo,
    promptInfo: propsPrompt,
  } = props;
  // 读取链接中的报告时间 / 语言，用于在订阅报告下拉中定位对应报告
  // （参考 AIInsightsReport/ReportGpt、CustomGpt 的链接定位逻辑）
  const location = useLocation();
  const {
    language: defaultLanguage,
    report_start_time,
    report_end_time,
  } = DBO_U.queryObj(location?.search);
  const { loading, filter, pageName } = useShortUrlFilterValue({ id: shortId });
  const { commentList, socialList, backCondOriginData } = useChannelShow();
  const [promptInfo, setPromptInfo] = useState<
    PromptConfirmItemType & {
      status?: string;
      start_time?: string;
      end_time?: string;
    }
  >(propsPrompt);

  // TODO: filter确实game id
  const filterParams = useMemo(
    () => ({
      ...filter,
      ...gameInfo,
      ...promptInfo,
      date_type: dayToDaily[reportItem?.interval_unit] || 'daily',
    }),
    [gameInfo, filter, promptInfo],
  );

  // defaultValue（链接中的报告时间）随 filterProps 透传给 ReportFilter → MultipleDropSelect，
  // 由其根据 start_time/end_time 在下拉中选中对应报告；故此处不再单独解构出 defaultValue。
  const {
    loading: pageLoading,
    isEmpty,
    language,
    setLanguage,
    gptLanguage,
    ...filterProps
  } = useCustomInsightReportList({
    gameInfo,
    task: reportItem,
    defaultLanguage,
    defaultValue: {
      start_time: report_start_time
        ? decodeURIComponent(report_start_time)
        : null,
      end_time: report_end_time ? decodeURIComponent(report_end_time) : null,
    },
  });

  const { emptyProps, getGPTEmptyProps } = useEmptyText({ promptInfo });
  const { formatMessage } = useLanguage();
  const dropdownRef = useRef<{ handleRecentReport: () => void; validReport: () => boolean; }>(null);
  const handleRecentReport = () => {
    dropdownRef?.current?.handleRecentReport();
  };
  const { options } = useReportLanguage({ gameInfo, setLanguage });

  return (
    <CardCommon
      loading={pageLoading || loading}
      loadingHeight={600}
      contentCard
      className="dbo-custom-ai-summary-preview-filter"
    >
      <PreviewFilter
        gameInfo={gameInfo}
        pageName={pageName as any}
        shortId={shortId}
        backCondOriginData={backCondOriginData}
        isAdvanced={promptInfo.gptOptions === 'advanced'}
        filterProps={filter}
        renderExcludeFilter={['date']}
      ></PreviewFilter>

      <CardCommon
        emptyHeight={600}
        empty={isEmpty}
        contentCard={!isEmpty}
        {...getGPTEmptyProps(reportItem)}
      >
        <>
          <ReportFilter
            {...filterProps}
            language={language}
            hasLanguage={propsPrompt.gptOptions === 'advanced'}
            confirm={(value) => {
              setPromptInfo({
                ...propsPrompt,
                taskId: value?.task_id,
                shortId: value?.short_url,
                status: value?.status || 'init',
                start_time: value?.start_time,
                end_time: value?.end_time,
              });
              // 切换报告时同步链接时间，便于复制 / 分享后重新打开仍定位到该报告
              updateURL({
                updateParams: {
                  report_start_time: value?.start_time || '',
                  report_end_time: value?.end_time || '',
                },
              });
            }}
            dropdownRef={dropdownRef}
            setLanguage={(value) => {
              setLanguage?.(value);
              updateURL({
                updateParams: { language: value },
              });
            }}
            options={options}
          ></ReportFilter>
          <CardCommon
            empty={promptInfo?.status !== 'success'}
            contentCard={promptInfo?.status === 'success'}
            emptyHeight={500}
            bodyStyle={{ marginTop: 20 }}
            emptyDescElement={
              dropdownRef?.current?.validReport ? (
                <Button
                  type="link"
                  style={{ paddingLeft: 5 }}
                  onClick={() => handleRecentReport?.()}
                >
                  {formatMessage({ id: 'Click to view recent report' })}
                </Button>
              ) : null
            }
            {...emptyProps}
          >
            <CustomGptBody
              filter={filterParams}
              contentRef={contentRef}
              commentList={commentList}
              socialList={socialList}
              promptInfo={promptInfo}
              gameInfo={gameInfo}
              hasStopBtn={false}
              wholeGameInfo={wholeGameInfo}
              scrolling={false}
              task={reportItem}
              openCardLoading
              language={gptLanguage}
              loading={false}
              isMini
              showStatsHeader
            ></CustomGptBody>
          </CardCommon>
        </>
      </CardCommon>
    </CardCommon>
  );
}

export function PreviewFilter({
  gameInfo,
  pageName,
  shortId,
  isAdvanced,
  backCondOriginData,
  filterProps,
  allPreview = true,
  operateShowOptions = ["more"],
  ...otherProps
}: {
  gameInfo: any;
  pageName: PageFilterKeys;
  shortId?: string;
  isAdvanced: boolean;
  backCondOriginData: (value: any) => void;
  filterProps?: any;
  allPreview?: boolean;
  [key: string]: any;
}) {
  const { formatMessage } = useLanguage();
  const { filterOptions } = useFilterDataHook({
    pageName,
    gameInfo,
  });

  const extraFilter = useMemo(() => {
    const filter: (keyof ExportDefaultType)[] = ['gptAgent', 'gptType'];
    if (isAdvanced) {
      filter.push('gptAnalysisTarget');
    }
    return filter;
  }, [isAdvanced]);

  if (!shortId) {
    return (
      <Card className={`card-common card-slot`}>
        <Card.Body>
          <div className="dbo-custom-ai-summary-preview-filter-empty">
            {formatMessage({
              id: 'This link does not contain a Shortid, so the filter options cannot be displayed.',
            })}
          </div>
        </Card.Body>
      </Card>
    );
  }
  return (
    <FilterFixed
      filter={[...filterOptions, ...extraFilter]}
      confirm={(values) => {
        // console.log('onConfirm values', values);
      }}
      isPreviews
      allPreview={allPreview}
      shortid={shortId}
      operateShowOptions={operateShowOptions}
      keyOperateShowOptions={[]}
      inheritStorageFilterValue={false}
      backCondOriginData={(value) => {
        backCondOriginData(value);
      }}
      {...otherProps}
    ></FilterFixed>
  );
}
